import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data)
};

// Users
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  getDepartments: () => api.get('/users/departments'),
  getDevices: (id) => api.get(`/users/${id}/devices`),
  getHistory: (id) => api.get(`/users/${id}/history`)
};

// Device Types
export const deviceTypeAPI = {
  getAll: (params) => api.get('/device-types', { params }),
  getById: (id) => api.get(`/device-types/${id}`),
  create: (data) => api.post('/device-types', data),
  update: (id, data) => api.put(`/device-types/${id}`, data),
  delete: (id) => api.delete(`/device-types/${id}`)
};

// Devices
export const deviceAPI = {
  getAll: (params) => api.get('/devices', { params }),
  getById: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  assign: (id, data) => api.post(`/devices/${id}/assign`, data),
  unassign: (id, data) => api.post(`/devices/${id}/unassign`, data),
  getStats: () => api.get('/devices/stats'),
  getLocations: () => api.get('/devices/locations'),
  getPDF: (id) => api.get(`/devices/${id}/pdf`, { responseType: 'blob' }),
  getListPDF: (params) => api.get('/devices/list-pdf', { params, responseType: 'blob' }),
  addSoftware: (id, data) => api.post(`/devices/${id}/software`, data),
  removeSoftware: (id, softwareId) => api.delete(`/devices/${id}/software/${softwareId}`)
};

// Request Types
export const requestTypeAPI = {
  getAll: (params) => api.get('/request-types', { params }),
  getById: (id) => api.get(`/request-types/${id}`),
  create: (data) => api.post('/request-types', data),
  update: (id, data) => api.put(`/request-types/${id}`, data),
  delete: (id) => api.delete(`/request-types/${id}`)
};

// Requests
export const requestAPI = {
  getAll: (params) => api.get('/requests', { params }),
  getById: (id) => api.get(`/requests/${id}`),
  create: (data) => api.post('/requests', data),
  update: (id, data) => api.put(`/requests/${id}`, data),
  assign: (id, data) => api.post(`/requests/${id}/assign`, data),
  getStats: () => api.get('/requests/stats')
};

// Replacements (Değişim Algoritması)
export const replacementAPI = {
  getSuggestions: () => api.get('/replacements'),
  getPriorities: () => api.get('/replacements/priorities')
};

// Documents (Zimmet Belgeleri)
export const documentAPI = {
  getByDevice: (deviceId) => api.get(`/documents/device/${deviceId}`),
  upload: (deviceId, formData) => api.post(`/documents/device/${deviceId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/documents/download/${id}`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`)
};

// Departments
export const departmentAPI = {
  getAll: (params) => api.get('/departments', { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`)
};

// Licenses
export const licenseAPI = {
  getAll: (params) => api.get('/licenses', { params }),
  getById: (id) => api.get(`/licenses/${id}`),
  create: (data) => api.post('/licenses', data),
  update: (id, data) => api.put(`/licenses/${id}`, data),
  delete: (id) => api.delete(`/licenses/${id}`),
  getStats: () => api.get('/licenses/stats'),
  getExpiring: () => api.get('/licenses/expiring'),
  assignToDevice: (id, data) => api.post(`/licenses/${id}/assign`, data),
  removeFromDevice: (id, deviceId) => api.delete(`/licenses/${id}/device/${deviceId}`)
};

// Maintenances
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenances', { params }),
  getById: (id) => api.get(`/maintenances/${id}`),
  create: (data) => api.post('/maintenances', data),
  update: (id, data) => api.put(`/maintenances/${id}`, data),
  delete: (id) => api.delete(`/maintenances/${id}`),
  getStats: () => api.get('/maintenances/stats'),
  getScheduled: () => api.get('/maintenances/scheduled'),
  getDeviceHistory: (deviceId) => api.get(`/maintenances/device/${deviceId}`)
};

// Notifications
export const notificationAPI = {
  getMyNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`)
};

// Stats (Dashboard)
export const statsAPI = {
  getDashboardStats: () => api.get('/stats/dashboard'),
  getMonthlyRequestStats: (params) => api.get('/stats/monthly-requests', { params }),
  getDevicesByType: () => api.get('/stats/devices-by-type'),
  getDevicesByDepartment: () => api.get('/stats/devices-by-department'),
  getRequestsByType: () => api.get('/stats/requests-by-type'),
  getAverageResolutionTime: () => api.get('/stats/resolution-time')
};

// Processor Rules (Islemci Kurallari)
export const processorRuleAPI = {
  getAll: () => api.get('/processor-rules'),
  getActive: () => api.get('/processor-rules/active'),
  getById: (id) => api.get(`/processor-rules/${id}`),
  create: (data) => api.post('/processor-rules', data),
  update: (id, data) => api.put(`/processor-rules/${id}`, data),
  delete: (id) => api.delete(`/processor-rules/${id}`),
  seedDefaults: () => api.post('/processor-rules/seed')
};

// Canias Requests
export const caniasRequestAPI = {
  getAll: (params) => api.get('/canias-requests', { params }),
  getById: (id) => api.get(`/canias-requests/${id}`),
  create: (data) => api.post('/canias-requests', data),
  update: (id, data) => api.put(`/canias-requests/${id}`, data),
  delete: (id) => api.delete(`/canias-requests/${id}`),
  getStats: () => api.get('/canias-requests/stats')
};

export default api;
